import { useContextStore } from '@/Stores/context';
import { normalizeDetailLinkMode } from '@/Utils/Wc/detailLinks';
import { normalizeWcRouterMode } from '@/Utils/Wc/routerConfig';
import { normalizeBasePath } from '@/Utils/Core/url';
import { findWcScript } from '@/Utils/Wc/wcScript';
import { ensureLoadedIfComplete } from './loaders';

interface BootstrapResult {
  language: string;
  experience: string;
  template: string;
  load: Awaited<ReturnType<typeof ensureLoadedIfComplete>>;
}

interface RouteParams {
  language?: unknown;
  experience?: unknown;
  template?: unknown;
}
interface BootstrapOptionsStandalone {
  mode: 'standalone';
  route?: { params?: RouteParams };
}
interface BootstrapOptionsWc {
  mode: 'wc';
  script?: HTMLScriptElement;
}
type Base = { awaitLoad?: boolean };
type BootstrapOptions = (BootstrapOptionsStandalone | BootstrapOptionsWc) & Base;

/**
 * Bootstraps contextual configuration (language / experience / template).
 * - WC mode: derives data-* attributes from the embedding script tag.
 * - Standalone: reads optional route params.
 * - Ensures context.init() is called if values were incomplete.
 * - Optionally awaits configuration loading (awaitLoad).
 */
export async function bootstrapConfig(opts: BootstrapOptions): Promise<BootstrapResult> {
  const context = useContextStore();

  const applyContextValues = (
    vals: Partial<Record<'language' | 'experience' | 'template', unknown>>,
  ) => {
    if (typeof vals.language === 'string' && vals.language) context.language = vals.language;
    if (typeof vals.experience === 'string' && vals.experience)
      context.experience = vals.experience;
    if (typeof vals.template === 'string' && vals.template) context.template = vals.template;
  };

  const needsInit = () => !context.language || !context.experience || !context.template;

  if (opts.mode === 'wc') {
    context.integrationMode = 'wc';
    const script = opts.script || findWcScript();
    if (!script) {
      console.warn('[bootstrapConfig] No WC script element found; falling back to context.init()');
    }
    const ds = script?.dataset;
    if (ds) {
      const rawDetailLinkMode = ds.detailLinkMode?.trim() ?? '';
      context.detailLinkMode = normalizeDetailLinkMode(rawDetailLinkMode);
      context.wcRouterMode = normalizeWcRouterMode(ds.routerMode);
      context.wcRouterBasePath =
        context.wcRouterMode === 'memory' ? '/' : normalizeBasePath(ds.basePath);
      if (rawDetailLinkMode && context.detailLinkMode === 'default') {
        console.warn(
          `[bootstrapConfig] Invalid detail link mode "${rawDetailLinkMode}". Falling back to the default link handling.`,
        );
      }
      applyContextValues({
        language: ds.language,
        experience: ds.experience,
        template: ds.template,
      });
    }
    if (needsInit()) {
      context.init();
    }
  } else {
    context.integrationMode = 'standalone';
    context.detailLinkMode = 'default';
    context.wcRouterMode = 'memory';
    context.wcRouterBasePath = '/';
    // Standalone mode: route params (if present) override existing context.
    const params = opts.route?.params;
    if (params) {
      applyContextValues({
        language: params.language,
        experience: params.experience,
        template: params.template,
      });
    }
    if (needsInit()) {
      context.init();
    }
  }

  const load = await ensureLoadedIfComplete();

  if (opts.awaitLoad && !load.valid) {
    console.warn('[bootstrapConfig] Configuration not fully loaded:', load.error || load.code);
  }

  return {
    language: context.language,
    experience: context.experience,
    template: context.template,
    load,
  };
}
